import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Input,
  Option,
  Select,
  Table,
  Tooltip,
  Typography,
} from '@mui/joy';
import { Trash as DeleteIcon } from '@phosphor-icons/react';
import type { CatalogUom, CatalogVariation } from '../../lib/distributorsApi';
import { SRS_CALL_BRANCH, SRS_CHECKOUT_DISCLAIMER } from './srsOrdering';
import { ABC_MAX_ORDER_LINES } from './abcConstants';

export type ApiCartLine = {
  lineId: string;
  notes?: string;
  item_number: string;
  description: string;
  supplier_name?: string;
  uomOptions: CatalogUom[];
  uom: string;
  quantity: number;
  is_dimensional: boolean;
  variations: CatalogVariation[];
  length_value?: number;
  length_uom?: string;
  image_asset_id?: string;
};

export type LinePriceState =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'priced'; unit_price: number; currency: string }
  | { state: 'error'; status_code: string; message: string };

interface Props {
  isSrs?: boolean;
  maxLines?: number;
  lines: ApiCartLine[];
  pricing: Record<string, LinePriceState>;
  onQuantityChange: (lineId: string, quantity: number) => void;
  onUomChange: (lineId: string, uom: string) => void;
  onLengthChange: (lineId: string, variationIndex: number) => void;
  onRemove: (lineId: string) => void;
  quoting: boolean;
}

export const cartTotals = (lines: ApiCartLine[], pricing: Record<string, LinePriceState>) => {
  let pricedSubtotal = 0;
  let pricedCount = 0;
  let errorCount = 0;
  let zeroCount = 0;
  let missingLengthCount = 0;
  lines.forEach((line) => {
    if (line.is_dimensional && line.length_value == null) missingLengthCount += 1;
    const price = pricing[line.lineId];
    if (!price) return;
    if (price.state === 'priced') {
      pricedCount += 1;
      pricedSubtotal += price.unit_price * line.quantity;
      if (price.unit_price === 0) zeroCount += 1;
    } else if (price.state === 'error') {
      errorCount += 1;
    }
  });
  return { pricedSubtotal, pricedCount, errorCount, zeroCount, missingLengthCount };
};

/** API-mode cart with per-line live-price states. */
export default function ApiOrderCart({
  lines,
  pricing,
  onQuantityChange,
  onUomChange,
  onLengthChange,
  onRemove,
  quoting,
  isSrs,
  maxLines = ABC_MAX_ORDER_LINES,
}: Props) {
  const totals = cartTotals(lines, pricing);

  const renderPriceCell = (line: ApiCartLine) => {
    const price = pricing[line.lineId];
    if (!price || price.state === 'idle') {
      return <Typography level="body-sm" color="neutral">—</Typography>;
    }
    if (price.state === 'pending') {
      return <CircularProgress size="sm" />;
    }
    if (isSrs && ((price.state === 'priced' && price.unit_price === 0) || (price.state === 'error' && price.message.includes('Please call branch')))) return <Typography level="body-sm" color="warning">{SRS_CALL_BRANCH}</Typography>;
    if (price.state === 'error') {
      return (
        <Tooltip title={price.message} variant="soft" color="danger">
          <Chip size="sm" color="danger" variant="soft">Not priced</Chip>
        </Tooltip>
      );
    }
    return (
      <Box>
        <Typography level="body-sm" noWrap>${price.unit_price.toFixed(2)}</Typography>
        {price.unit_price === 0 && (
          // Own line, never inline: at $0.00 this chip is wider than the price
          // column and used to spill over the Total cell.
          <Chip
            size="sm"
            color="primary"
            variant="soft"
            sx={{ mt: 0.25, maxWidth: '100%', '--Chip-paddingInline': '6px' }}
          >
            Confirm with branch
          </Chip>
        )}
      </Box>
    );
  };

  const renderLineTotal = (line: ApiCartLine) => {
    const price = pricing[line.lineId];
    if (price?.state !== 'priced') {
      return <Typography level="body-sm" color="neutral">—</Typography>;
    }
    return (
      <Typography level="body-sm" noWrap>
        ${(price.unit_price * line.quantity).toFixed(2)}
      </Typography>
    );
  };

  return (
    <Box>
      {isSrs && <Alert color="neutral" sx={{ mb: 2 }}>{SRS_CHECKOUT_DISCLAIMER}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography level="body-sm" color="neutral">
          {lines.length} / {maxLines} lines
        </Typography>
        {quoting && (
          <Typography level="body-xs" color="neutral" startDecorator={<CircularProgress size="sm" />}>
            Updating prices…
          </Typography>
        )}
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="sm"
          sx={{
            mb: 1,
            minWidth: 620,
            // Honour the per-column widths above instead of letting long
            // product names dictate the layout.
            tableLayout: 'fixed',
            '& td': { verticalAlign: 'top' },
          }}
        >
          <thead>
            {/* Product is the only flexible column — every other one gets a
                fixed width just wide enough for its control, so the row can
                never overflow the card (which used to push Total off-screen)
                and the $0.00 "Confirm with branch" chip has room to sit under
                the price instead of spilling into Total. */}
            <tr>
              <th style={{ minWidth: 180 }}>Product</th>
              <th style={{ width: 76 }}>UOM</th>
              <th style={{ width: 104 }}>Length</th>
              <th style={{ width: 72 }}>Qty</th>
              <th style={{ width: 104 }}>Unit price</th>
              <th style={{ width: 92 }}>Total</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const missingLength = line.is_dimensional && line.length_value == null;
              const selectedVariation = line.variations.findIndex(
                (variation) => variation.value === line.length_value && variation.uom === (line.length_uom || variation.uom),
              );
              return (
                <tr key={line.lineId}>
                  <td>
                    <Typography level="body-sm" fontWeight="lg">{line.description}</Typography>
                    <Typography level="body-xs" color="neutral">{line.item_number}</Typography>
                  </td>
                  <td>
                    <Select
                      size="sm"
                      value={line.uom}
                      onChange={(_event, value) => value && onUomChange(line.lineId, value)}
                      disabled={line.uomOptions.length <= 1}
                    >
                      {(line.uomOptions.length > 0 ? line.uomOptions : [{ code: line.uom, name: line.uom, description: '', is_stocking: true }]).map((uom) => (
                        <Option key={uom.code} value={uom.code}>{uom.code}</Option>
                      ))}
                    </Select>
                  </td>
                  <td>
                    {line.is_dimensional ? (
                      <Select
                        size="sm"
                        color={missingLength ? 'danger' : 'neutral'}
                        value={selectedVariation >= 0 ? selectedVariation : null}
                        placeholder="Required"
                        onChange={(_event, value) => value !== null && onLengthChange(line.lineId, value as number)}
                      >
                        {line.variations.map((variation, index) => (
                          <Option key={`${variation.value}-${variation.uom}`} value={index}>
                            {variation.size || `${variation.value} ${variation.uom}`}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <Typography level="body-sm" color="neutral">—</Typography>
                    )}
                  </td>
                  <td>
                    <Input
                      size="sm"
                      type="number"
                      value={line.quantity}
                      slotProps={{ input: { min: 1 } }}
                      onChange={(event) =>
                        onQuantityChange(line.lineId, Math.max(0, parseInt(event.target.value, 10) || 0))
                      }
                      sx={{ width: 70 }}
                    />
                  </td>
                  <td>{renderPriceCell(line)}</td>
                  <td>{renderLineTotal(line)}</td>
                  <td>
                    <IconButton size="sm" color="danger" onClick={() => onRemove(line.lineId)}>
                      <DeleteIcon />
                    </IconButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>
                <Typography level="body-md" fontWeight="lg">
                  Priced subtotal ({totals.pricedCount} of {lines.length} lines):
                </Typography>
              </td>
              <td colSpan={2}>
                <Typography level="body-md" fontWeight="lg">
                  ${totals.pricedSubtotal.toFixed(2)}
                </Typography>
              </td>
            </tr>
          </tfoot>
        </Table>
      </Box>

      {totals.errorCount > 0 && (
        <Alert color="warning" variant="soft" sx={{ mb: 1 }}>
          {totals.errorCount} line{totals.errorCount === 1 ? '' : 's'} could not be priced —
          remove or adjust them before submitting.
        </Alert>
      )}
      {totals.missingLengthCount > 0 && (
        <Alert color="warning" variant="soft" sx={{ mb: 1 }}>
          Pick a length for the cut-to-length line{totals.missingLengthCount === 1 ? '' : 's'} to get pricing.
        </Alert>
      )}
      {totals.zeroCount > 0 && (
        <Alert color="primary" variant="soft" sx={{ mb: 1 }}>
          {totals.zeroCount} line{totals.zeroCount === 1 ? ' shows' : 's show'} $0.00 — that's a
          valid ABC response meaning the branch confirms pricing on receipt.
        </Alert>
      )}
    </Box>
  );
}
